import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trophy, Vote, Megaphone, ChevronRight, Award } from "lucide-react";
import { api, fileUrl } from "../lib/api";

export const BADGE_LABELS = { nominated: "Nominated", finalist: "Finalist", winner: "Winner", category_winner: "Category Winner", artist_of_year: "Artist of the Year", venue_of_year: "Venue of the Year" };

const StatusPill = ({ flags }) => {
  if (flags.results_published) return <span className="text-xs px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 font-bold">Results published</span>;
  if (flags.voting_open) return <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">Voting open</span>;
  if (flags.nominations_open) return <span className="text-xs px-3 py-1 rounded-full bg-sky-500/20 text-sky-300 font-bold">Nominations open</span>;
  return <span className="text-xs px-3 py-1 rounded-full bg-zinc-500/20 text-zinc-300 font-bold">Coming soon</span>;
};

function SeasonView({ slug }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/awards/seasons/${slug}`).then((r) => setS(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <p className="text-zinc-500 py-20 text-center">Season not found.</p>;
  if (!s) return <p className="text-zinc-500 py-20 text-center">Loading…</p>;
  const groups = {};
  (s.categories || []).forEach((c) => (groups[c.entity_type] = groups[c.entity_type] || []).push(c));
  const labels = { artist: "Artist categories", venue: "Venue categories", local_favorite: "Local favorites", event: "Event categories" };
  return (
    <div data-testid="award-season-page">
      <div className="relative rounded-3xl overflow-hidden mb-10">
        {s.hero_image && <img src={fileUrl(s.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className={`absolute inset-0 ${s.hero_image ? "bg-black/70" : "bg-gradient-to-r from-amber-500/10 to-rose-500/10"}`} />
        <div className="relative p-8 md:p-14">
          <p className="overline mb-2 flex items-center gap-2"><Trophy size={14} className="text-amber-400" /> The VIP Spotlight Awards</p>
          <h1 className="font-display font-black text-4xl md:text-6xl">{s.name}</h1>
          {s.description && <p className="text-zinc-300 mt-3 max-w-2xl">{s.description}</p>}
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <StatusPill flags={s.flags} />
            {s.flags.nominations_open && s.nomination_end && <span className="text-xs text-zinc-400">Nominations close {s.nomination_end}</span>}
            {s.flags.voting_open && s.voting_end && <span className="text-xs text-zinc-400">Voting closes {s.voting_end}</span>}
          </div>
          {(s.flags.nominations_open || s.flags.voting_open) && (
            <div className="flex flex-wrap gap-3 mt-6">
              {s.flags.nominations_open && <Link to="/artists" className="bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="awards-cta-nominate"><Megaphone size={15} /> Nominate your favorites</Link>}
              {s.flags.voting_open && <span className="border border-emerald-400/40 text-emerald-300 font-bold px-5 py-2.5 rounded-full text-sm flex items-center gap-2"><Vote size={15} /> Pick a category below to vote</span>}
            </div>
          )}
        </div>
      </div>

      {s.flags.results_published && Object.keys(s.results || {}).length > 0 && (
        <div className="mb-12">
          <h2 className="font-display font-extrabold text-3xl mb-6 flex items-center gap-2"><Trophy className="text-amber-400" size={24} /> Winners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(s.results).map(([cid, r]) => (
              <Link key={cid} to={`/${r.winner?.coll}/${r.winner?.slug}`} className="bg-[#111] border border-amber-400/30 rounded-2xl p-5 hover:border-amber-400/60 transition-colors" data-testid={`award-winner-${cid}`}>
                <p className="overline mb-2">{r.category_name}</p>
                <div className="flex items-center gap-3">
                  {r.winner?.image ? <img src={fileUrl(r.winner.image)} alt="" className="w-14 h-14 rounded-xl object-cover" /> : <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center"><Award size={22} className="text-amber-400" /></div>}
                  <div><p className="font-display font-bold text-lg">{r.winner?.name}</p>
                    {s.settings?.public_vote_counts && <p className="text-xs text-zinc-500">{r.winner?.votes} votes</p>}</div>
                </div>
                {r.finalists?.length > 0 && <p className="text-xs text-zinc-500 mt-3">Finalists: {r.finalists.map((f) => f.name).join(", ")}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {Object.entries(labels).map(([k, label]) => groups[k]?.length > 0 && (
        <div key={k} className="mb-10">
          <h2 className="font-display font-extrabold text-2xl mb-5">{label}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups[k].map((c) => (
              <Link key={c.id} to={`/awards/${s.slug}/${c.slug}`} className="bg-[#111] border border-white/10 rounded-2xl p-5 hover:border-rose-500/40 transition-colors flex items-center justify-between gap-3" data-testid={`award-cat-${c.slug}`}>
                <div><p className="font-display font-bold text-lg">{c.name}</p>
                  <p className="text-xs text-zinc-500 mt-1">{c.nominee_count} nominee{c.nominee_count === 1 ? "" : "s"}{c.description ? ` · ${c.description}` : ""}</p></div>
                <ChevronRight size={18} className="text-zinc-600 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      ))}
      {(s.categories || []).length === 0 && <p className="text-zinc-500 text-center py-10">Categories coming soon.</p>}
    </div>
  );
}

export default function Awards() {
  const { season } = useParams();
  const [seasons, setSeasons] = useState(null);
  useEffect(() => { if (!season) api.get("/awards/seasons").then((r) => setSeasons(r.data)).catch(() => setSeasons([])); }, [season]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="awards-page">
      {season ? <SeasonView slug={season} /> : (
        <>
          <p className="overline mb-2 flex items-center gap-2"><Trophy size={14} className="text-amber-400" /> Celebrate the coast</p>
          <h1 className="font-display font-black text-4xl md:text-6xl">The VIP Spotlight Awards</h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">The VIP Spotlight Awards celebrate the artists, venues, and local favorites that make the Gulf Coast entertainment scene shine.</p>
          <div className="mt-10 space-y-4">
            {seasons === null ? <p className="text-zinc-500">Loading…</p> : seasons.length === 0 ? (
              <p className="text-zinc-500" data-testid="awards-empty">No award seasons yet — check back soon!</p>
            ) : seasons.map((s) => (
              <Link key={s.id} to={`/awards/${s.slug}`} className="block bg-[#111] border border-white/10 rounded-2xl p-6 hover:border-amber-400/40 transition-colors" data-testid={`award-season-${s.slug}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h2 className="font-display font-bold text-2xl">{s.name}</h2>
                    {s.description && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{s.description}</p>}</div>
                  <StatusPill flags={s.flags} />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
